/**
 * Saved-view URL params, localStorage filters, and queue URL builders.
 */
import { leadFactoryMetricsDaysStorageKey, leadFactorySavedViewScopes } from "./dashboard-urls.js";
import {
  contactWorkDefaultCallDailyCapacity,
  contactWorkDefaultLetterDailyCapacity,
  contactWorkDefaultResearchDailyCapacity,
  contactWorkDefaultPlanTargetDays,
  normalizedContactWorkDailyCapacity,
  normalizedContactWorkOperatorRoster,
  normalizedContactWorkTargetDays
} from "./dashboard-contact-work.js";
import type {
  AcquisitionPipelineSavedViewFilters,
  AcquisitionWorkSavedViewFilters,
  AssetDispositionSavedViewFilters,
  AssetWorkSavedViewFilters,
  ContactQueueSavedViewFilters,
  ContactWorkSavedViewFilters,
  LeadInventorySavedViewFilters,
  ReviewQueueSavedViewFilters,
  SavedViewSortDirection,
  SavedViewSortFilters
} from "./dashboard-lead-factory-types.js";

type LeadFactorySavedViewScope = (typeof leadFactorySavedViewScopes)[number];
const leadFactorySavedViewStoragePrefix = "county-lead-factory-saved-view-filters";
const savedViewSortDirections = ["asc", "desc"] as const;

export function getLeadFactoryMetricsDays(): number {
  const urlDays = Number(new URLSearchParams(window.location.search).get("lf_days"));
  if (Number.isFinite(urlDays) && urlDays > 0) {
    const boundedUrlDays = Math.min(Math.max(Math.floor(urlDays), 1), 90);
    setLeadFactoryMetricsDays(boundedUrlDays);
    return boundedUrlDays;
  }
  const value = Number(window.localStorage.getItem(leadFactoryMetricsDaysStorageKey) ?? 14);
  if (!Number.isFinite(value)) return 14;
  return Math.min(Math.max(Math.floor(value), 1), 90);
}

export function setLeadFactoryMetricsDays(days: number): void {
  window.localStorage.setItem(leadFactoryMetricsDaysStorageKey, String(Math.min(Math.max(Math.floor(days), 1), 90)));
}

export function leadFactoryWindowUrl(url: string, days: number): string {
  const parsed = new URL(url, window.location.origin);
  parsed.searchParams.set("days", String(days));
  return url.startsWith("http://") || url.startsWith("https://") ? parsed.toString() : `${parsed.pathname}${parsed.search}`;
}

export function leadFactoryUrlWithParams(url: string, params: Record<string, string | number | null | undefined>): string {
  const parsed = new URL(url, window.location.origin);
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "" || value === "all") {
      parsed.searchParams.delete(key);
    } else {
      parsed.searchParams.set(key, String(value));
    }
  }
  return url.startsWith("http://") || url.startsWith("https://") ? parsed.toString() : `${parsed.pathname}${parsed.search}`;
}

export function savedViewStorageKey(scope: LeadFactorySavedViewScope): string {
  return `${leadFactorySavedViewStoragePrefix}:${scope}`;
}

function storedSavedViewFilters<T extends object>(scope: LeadFactorySavedViewScope, defaults: T): T {
  try {
    const stored = window.localStorage.getItem(savedViewStorageKey(scope));
    if (!stored) return { ...defaults };
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? { ...defaults, ...parsed } : { ...defaults };
  } catch {
    return { ...defaults };
  }
}

export function setSavedViewFilters(scope: LeadFactorySavedViewScope, filters: Record<string, unknown>): void {
  window.localStorage.setItem(savedViewStorageKey(scope), JSON.stringify(filters));
}

export function savedViewUrlParam(scope: LeadFactorySavedViewScope): string {
  const params: Record<LeadFactorySavedViewScope, string> = {
    business_metrics: "lf_metrics_view",
    lead_inventory: "lf_lead_view",
    review_queue: "lf_review_view",
    contact_queue: "lf_contact_view",
    contact_work: "lf_contact_work_view",
    acquisition_pipeline: "lf_pipeline_view",
    acquisition_work: "lf_acquisition_work_view",
    asset_work: "lf_asset_work_view",
    asset_disposition: "lf_asset_disposition_view"
  };
  return params[scope];
}

export function directFilterUrlParams(scope: LeadFactorySavedViewScope): string[] {
  const params: Record<LeadFactorySavedViewScope, string[]> = {
    business_metrics: ["lf_days"],
    lead_inventory: ["lf_lead_id", "lf_lead_status", "lf_lead_assigned", "lf_lead_limit", "lf_lead_sort", "lf_lead_sort_dir"],
    review_queue: ["lf_review_triage", "lf_review_assigned", "lf_review_limit", "lf_review_sort", "lf_review_sort_dir"],
    contact_queue: ["lf_contact_readiness", "lf_contact_scope", "lf_contact_exclude_test", "lf_contact_limit", "lf_contact_sort", "lf_contact_sort_dir"],
    contact_work: [
      "lf_contact_work_queue",
      "lf_contact_work_letter_kind",
      "lf_contact_work_scope",
      "lf_contact_work_exclude_test",
      "lf_contact_work_actionable_only",
      "lf_contact_work_assigned",
      "lf_contact_work_target_days",
      "lf_contact_work_call_capacity",
      "lf_contact_work_letter_capacity",
      "lf_contact_work_research_capacity",
      "lf_contact_work_call_roster",
      "lf_contact_work_letter_roster",
      "lf_contact_work_research_roster",
      "lf_contact_work_limit",
      "lf_contact_work_sort",
      "lf_contact_work_sort_dir"
    ],
    acquisition_pipeline: ["lf_pipeline_stage", "lf_pipeline_limit", "lf_pipeline_sort", "lf_pipeline_sort_dir"],
    acquisition_work: ["lf_acquisition_work_queue", "lf_acquisition_work_limit", "lf_acquisition_work_sort", "lf_acquisition_work_sort_dir"],
    asset_work: ["lf_asset_task_status", "lf_asset_task_type", "lf_asset_task_limit", "lf_asset_task_sort", "lf_asset_task_sort_dir"],
    asset_disposition: ["lf_asset_disposition_status", "lf_asset_disposition_strategy", "lf_asset_disposition_limit", "lf_asset_disposition_sort", "lf_asset_disposition_sort_dir"]
  };
  return params[scope];
}

export function setSavedViewUrl(scope: LeadFactorySavedViewScope, viewId: string): string {
  const url = new URL(window.location.href);
  url.searchParams.delete("lf_view");
  url.searchParams.set(savedViewUrlParam(scope), viewId);
  directFilterUrlParams(scope).forEach((param) => url.searchParams.delete(param));
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  return url.toString();
}

export function normalizedLimit(value: unknown, defaultLimit: number, maxLimit: number): number {
  const limit = Number(value ?? defaultLimit);
  if (!Number.isInteger(limit) || limit <= 0) return defaultLimit;
  return Math.min(limit, maxLimit);
}

export function stringParam(name: string): string {
  return new URLSearchParams(window.location.search).get(name)?.trim() ?? "";
}

export function savedSortDirection(value: unknown): SavedViewSortDirection {
  return savedViewSortDirections.includes(value as SavedViewSortDirection) ? (value as SavedViewSortDirection) : "desc";
}

export function savedSortUrlParams(filters: SavedViewSortFilters): { sort_by: string; sort_direction: SavedViewSortDirection | null } {
  return {
    sort_by: filters.sortBy,
    sort_direction: filters.sortBy ? filters.sortDirection : null
  };
}

export function getLeadInventoryFilters(): LeadInventorySavedViewFilters {
  const stored = storedSavedViewFilters<LeadInventorySavedViewFilters>("lead_inventory", { leadId: "", status: "", assignedTo: "", limit: 75, sortBy: "", sortDirection: "desc" });
  return {
    leadId: stringParam("lf_lead_id") || String(stored.leadId ?? ""),
    status: stringParam("lf_lead_status") || String(stored.status ?? ""),
    assignedTo: stringParam("lf_lead_assigned") || String(stored.assignedTo ?? ""),
    limit: normalizedLimit(stringParam("lf_lead_limit") || stored.limit, 75, 500),
    sortBy: stringParam("lf_lead_sort") || String(stored.sortBy ?? ""),
    sortDirection: savedSortDirection(stringParam("lf_lead_sort_dir") || stored.sortDirection)
  };
}

export function getReviewQueueFilters(): ReviewQueueSavedViewFilters {
  const stored = storedSavedViewFilters<ReviewQueueSavedViewFilters>("review_queue", { triageState: "", assignedTo: "", limit: 75, sortBy: "", sortDirection: "desc" });
  return {
    triageState: stringParam("lf_review_triage") || String(stored.triageState ?? ""),
    assignedTo: stringParam("lf_review_assigned") || String(stored.assignedTo ?? ""),
    limit: normalizedLimit(stringParam("lf_review_limit") || stored.limit, 75, 250),
    sortBy: stringParam("lf_review_sort") || String(stored.sortBy ?? ""),
    sortDirection: savedSortDirection(stringParam("lf_review_sort_dir") || stored.sortDirection)
  };
}

export function getContactQueueFilters(): ContactQueueSavedViewFilters {
  const stored = storedSavedViewFilters<ContactQueueSavedViewFilters>("contact_queue", {
    readiness: "",
    scope: "lead_supply",
    excludeTest: "true",
    limit: 75,
    sortBy: "",
    sortDirection: "desc"
  });
  return {
    readiness: stringParam("lf_contact_readiness") || String(stored.readiness ?? ""),
    scope: stringParam("lf_contact_scope") || String(stored.scope ?? "lead_supply"),
    excludeTest: stringParam("lf_contact_exclude_test") || String(stored.excludeTest ?? "true"),
    limit: normalizedLimit(stringParam("lf_contact_limit") || stored.limit, 75, 500),
    sortBy: stringParam("lf_contact_sort") || String(stored.sortBy ?? ""),
    sortDirection: savedSortDirection(stringParam("lf_contact_sort_dir") || stored.sortDirection)
  };
}

export function getContactWorkFilters(): ContactWorkSavedViewFilters {
  const stored = storedSavedViewFilters<ContactWorkSavedViewFilters>("contact_work", {
    leadId: "",
    queue: "all",
    letterKind: "all",
    scope: "lead_supply",
    excludeTest: "true",
    actionableOnly: "true",
    assignedTo: "",
    targetDays: contactWorkDefaultPlanTargetDays,
    callDailyCapacity: contactWorkDefaultCallDailyCapacity,
    letterDailyCapacity: contactWorkDefaultLetterDailyCapacity,
    researchDailyCapacity: contactWorkDefaultResearchDailyCapacity,
    callOperatorRoster: "",
    letterOperatorRoster: "",
    researchOperatorRoster: "",
    limit: 75,
    sortBy: "",
    sortDirection: "desc"
  });
  return {
    leadId: stringParam("lf_lead_id") || String(stored.leadId ?? ""),
    queue: stringParam("lf_contact_work_queue") || String(stored.queue ?? "all"),
    letterKind: stringParam("lf_contact_work_letter_kind") || String(stored.letterKind ?? "all"),
    scope: stringParam("lf_contact_work_scope") || String(stored.scope ?? "lead_supply"),
    excludeTest: stringParam("lf_contact_work_exclude_test") || String(stored.excludeTest ?? "true"),
    actionableOnly: stringParam("lf_contact_work_actionable_only") || String(stored.actionableOnly ?? "true"),
    assignedTo: stringParam("lf_contact_work_assigned") || String(stored.assignedTo ?? ""),
    targetDays: normalizedContactWorkTargetDays(stringParam("lf_contact_work_target_days") || stored.targetDays),
    callDailyCapacity: normalizedContactWorkDailyCapacity(
      stringParam("lf_contact_work_call_capacity") || stored.callDailyCapacity,
      contactWorkDefaultCallDailyCapacity
    ),
    letterDailyCapacity: normalizedContactWorkDailyCapacity(
      stringParam("lf_contact_work_letter_capacity") || stored.letterDailyCapacity,
      contactWorkDefaultLetterDailyCapacity
    ),
    researchDailyCapacity: normalizedContactWorkDailyCapacity(
      stringParam("lf_contact_work_research_capacity") || stored.researchDailyCapacity,
      contactWorkDefaultResearchDailyCapacity
    ),
    callOperatorRoster: normalizedContactWorkOperatorRoster(
      stringParam("lf_contact_work_call_roster") || stored.callOperatorRoster
    ),
    letterOperatorRoster: normalizedContactWorkOperatorRoster(
      stringParam("lf_contact_work_letter_roster") || stored.letterOperatorRoster
    ),
    researchOperatorRoster: normalizedContactWorkOperatorRoster(
      stringParam("lf_contact_work_research_roster") || stored.researchOperatorRoster
    ),
    limit: normalizedLimit(stringParam("lf_contact_work_limit") || stored.limit, 75, 500),
    sortBy: stringParam("lf_contact_work_sort") || String(stored.sortBy ?? ""),
    sortDirection: savedSortDirection(stringParam("lf_contact_work_sort_dir") || stored.sortDirection)
  };
}

export function getAcquisitionPipelineFilters(): AcquisitionPipelineSavedViewFilters {
  const stored = storedSavedViewFilters<AcquisitionPipelineSavedViewFilters>("acquisition_pipeline", { stage: "", limit: 75, sortBy: "", sortDirection: "desc" });
  return {
    stage: stringParam("lf_pipeline_stage") || String(stored.stage ?? ""),
    limit: normalizedLimit(stringParam("lf_pipeline_limit") || stored.limit, 75, 500),
    sortBy: stringParam("lf_pipeline_sort") || String(stored.sortBy ?? ""),
    sortDirection: savedSortDirection(stringParam("lf_pipeline_sort_dir") || stored.sortDirection)
  };
}

export function getAcquisitionWorkFilters(): AcquisitionWorkSavedViewFilters {
  const stored = storedSavedViewFilters<AcquisitionWorkSavedViewFilters>("acquisition_work", { queue: "all", limit: 75, sortBy: "", sortDirection: "desc" });
  return {
    queue: stringParam("lf_acquisition_work_queue") || String(stored.queue ?? "all"),
    limit: normalizedLimit(stringParam("lf_acquisition_work_limit") || stored.limit, 75, 500),
    sortBy: stringParam("lf_acquisition_work_sort") || String(stored.sortBy ?? ""),
    sortDirection: savedSortDirection(stringParam("lf_acquisition_work_sort_dir") || stored.sortDirection)
  };
}

export function getAssetWorkFilters(): AssetWorkSavedViewFilters {
  const stored = storedSavedViewFilters<AssetWorkSavedViewFilters>("asset_work", { status: "assigned", taskType: "", limit: 75, sortBy: "", sortDirection: "desc" });
  return {
    status: stringParam("lf_asset_task_status") || String(stored.status ?? "assigned"),
    taskType: stringParam("lf_asset_task_type") || String(stored.taskType ?? ""),
    limit: normalizedLimit(stringParam("lf_asset_task_limit") || stored.limit, 75, 500),
    sortBy: stringParam("lf_asset_task_sort") || String(stored.sortBy ?? ""),
    sortDirection: savedSortDirection(stringParam("lf_asset_task_sort_dir") || stored.sortDirection)
  };
}

export function getAssetDispositionFilters(): AssetDispositionSavedViewFilters {
  const stored = storedSavedViewFilters<AssetDispositionSavedViewFilters>("asset_disposition", { status: "", strategy: "", limit: 75, sortBy: "", sortDirection: "desc" });
  return {
    status: stringParam("lf_asset_disposition_status") || String(stored.status ?? ""),
    strategy: stringParam("lf_asset_disposition_strategy") || String(stored.strategy ?? ""),
    limit: normalizedLimit(stringParam("lf_asset_disposition_limit") || stored.limit, 75, 500),
    sortBy: stringParam("lf_asset_disposition_sort") || String(stored.sortBy ?? ""),
    sortDirection: savedSortDirection(stringParam("lf_asset_disposition_sort_dir") || stored.sortDirection)
  };
}

export function leadInventoryUrl(url: string, filters: LeadInventorySavedViewFilters): string {
  return leadFactoryUrlWithParams(url, { lead_id: filters.leadId, status: filters.status, assigned_to: filters.assignedTo, limit: filters.limit, ...savedSortUrlParams(filters) });
}

export function reviewQueueUrl(url: string, filters: ReviewQueueSavedViewFilters): string {
  return leadFactoryUrlWithParams(url, { triage_state: filters.triageState, assigned_to: filters.assignedTo, limit: filters.limit, ...savedSortUrlParams(filters) });
}

export function contactQueueUrl(url: string, filters: ContactQueueSavedViewFilters): string {
  return leadFactoryUrlWithParams(url, {
    readiness: filters.readiness,
    scope: filters.scope,
    exclude_test: filters.excludeTest,
    limit: filters.limit,
    ...savedSortUrlParams(filters)
  });
}

export function contactWorkQueueUrl(url: string, filters: ContactWorkSavedViewFilters): string {
  return leadFactoryUrlWithParams(url, {
    lead_id: filters.leadId,
    queue: filters.queue,
    letter_kind: filters.letterKind,
    scope: filters.scope,
    exclude_test: filters.excludeTest,
    actionable_only: filters.actionableOnly,
    assigned_to: filters.assignedTo,
    limit: filters.limit,
    ...savedSortUrlParams(filters)
  });
}

export function contactWorkCapacityUrl(url: string, filters: ContactWorkSavedViewFilters): string {
  return leadFactoryUrlWithParams(url, {
    lead_id: filters.leadId,
    queue: filters.queue,
    letter_kind: filters.letterKind,
    scope: filters.scope,
    exclude_test: filters.excludeTest,
    actionable_only: filters.actionableOnly,
    assigned_to: filters.assignedTo,
    limit: 1000
  });
}

export function contactWorkThroughputUrl(url: string, filters: ContactWorkSavedViewFilters): string {
  return leadFactoryUrlWithParams(url, {
    lead_id: filters.leadId,
    queue: filters.queue,
    letter_kind: filters.letterKind,
    scope: filters.scope,
    exclude_test: filters.excludeTest,
    actionable_only: filters.actionableOnly,
    assigned_to: filters.assignedTo,
    days: filters.targetDays,
    limit: 1000
  });
}

export function contactWorkOperatorEventsUrl(url: string, filters: ContactWorkSavedViewFilters): string {
  return leadFactoryUrlWithParams(url, {
    assigned_to: filters.assignedTo,
    days: filters.targetDays,
    limit: 25
  });
}

export function contactWorkOperatorOutcomesUrl(url: string, filters: ContactWorkSavedViewFilters): string {
  return leadFactoryUrlWithParams(url, {
    lead_id: filters.leadId,
    queue: filters.queue,
    scope: filters.scope,
    exclude_test: filters.excludeTest,
    assigned_to: filters.assignedTo,
    days: filters.targetDays,
    limit: 25
  });
}

export function followUpQueueUrl(url: string): string {
  return leadFactoryUrlWithParams(url, { task_id: stringParam("lf_follow_up_task_id") });
}

export function acquisitionPipelineUrl(url: string, filters: AcquisitionPipelineSavedViewFilters): string {
  return leadFactoryUrlWithParams(url, { stage: filters.stage, limit: filters.limit, ...savedSortUrlParams(filters) });
}

export function acquisitionWorkQueueUrl(url: string, filters: AcquisitionWorkSavedViewFilters): string {
  return leadFactoryUrlWithParams(url, { queue: filters.queue, limit: filters.limit, ...savedSortUrlParams(filters) });
}

export function assetWorkUrl(url: string, filters: AssetWorkSavedViewFilters): string {
  return leadFactoryUrlWithParams(url, { status: filters.status, taskType: filters.taskType, limit: filters.limit, ...savedSortUrlParams(filters) });
}

export function assetDispositionUrl(url: string, filters: AssetDispositionSavedViewFilters): string {
  return leadFactoryUrlWithParams(url, { status: filters.status, strategy: filters.strategy, limit: filters.limit, ...savedSortUrlParams(filters) });
}

